import {
  AbroadFeesResponseItem,
  CalculateSwpMonthsParams,
  FeePaidTable,
  GetAllGoalsResponse,
  Plan,
  RetirementBucketAllocation,
  RetirementBucketAmounts,
  RetirementPlanCardData,
  RetirementPlanCardRow,
  SchoolFeeProjectionRow,
  SchoolOptions,
  SipGrowthTable,
} from "@/type/financialPageTypes";
import { apiGet, apiPost } from "./apiClient";
import { formatIndianNumber } from "./commonFunctions";

// Get Al Goals API Response
export async function getAllGoals(): Promise<GetAllGoalsResponse | null> {
  try {
    const response = await apiGet<GetAllGoalsResponse>("getAllGoals");
    return response;
  } catch (error) {
    console.error("Failed to fetch goals:", error);
    return null; // or throw error if you want the page to fail
  }
}

// Fetch All Collegr Lists
export async function fetchCountryList(gradu_type: string, course: string) {
  try {
    const payload = {
      gradu_type,
      course,
    };
    const res = await apiPost("countryByCourse", payload);

    if (!Array.isArray(res) || res.length <= 0) return [];

    return res;
  } catch (err) {
    console.error("College API failed:", err);
    return [];
  }
}

// Fetch All Collegr Lists
export async function fetchCollegeList(gradu_type: string, course: string, country_name: string) {
  try {
    const res = await apiPost<{ status: number; result: AbroadFeesResponseItem[] }>("abroadFeesExpense", {
      gradu_type,
      course,
      country_name,
    });

    if (res?.status !== 1 || !Array.isArray(res.result)) return [];

    return res.result.map((u) => ({
      name: u.university_name,
      university_logo: u.university_logo || "/next_assets/images/financial-planning/default-college.png",
      fees: String(u.course_fees_inr) || "N/A",
    }));
  } catch (err) {
    console.error("College API failed:", err);
    return [];
  }
}

// Calculate SIP Values
export const calculateSIPValue = (sip: number, years = 1, annualReturn = 0.12) => {
  annualReturn = years <= 1 ? 0.08 : years <= 3 ? 0.1 : years <= 5 ? 0.12 : 0.15;

  const months = years * 12;
  const monthlyRate = annualReturn / 12;
  let total = 0;
  for (let month = 1; month <= months; month++) {
    total += sip * Math.pow(1 + monthlyRate, months - month);
  }
  return total;
};

// Calculate SIP Values
export const calculateSipForGoal = (goalAmount: number, years = 1, annualReturn = 0.12) => {
  annualReturn = years <= 1 ? 0.08 : years <= 3 ? 0.1 : years <= 5 ? 0.12 : 0.15;

  if (goalAmount <= 0 || years <= 0) return 0;
  const months = years * 12;
  const monthlyRate = annualReturn / 12;
  if (monthlyRate === 0) return Math.round(goalAmount / months);
  const factor = Math.pow(1 + monthlyRate, months) - 1;
  if (factor <= 0) return 0;
  return Math.round((goalAmount * monthlyRate) / factor);
};

// Get Year From Selected Year
export const getYearsFromSelectedYear = (selectedYear?: number) => {
  if (!selectedYear) return 0;
  const currentYear = new Date().getFullYear();
  return Math.max(selectedYear - currentYear, 1);
};

// Get Grouped By Class Indexs
export const getGroupByClassIndex = (idx: number) => {
  if (idx >= -4 && idx <= 0) return { label: "Playgroup", color: "bg-[#eef4ff] text-primaryBlue" };
  if (idx >= 1 && idx <= 3) return { label: "Primary", color: "bg-[#D6C3FF] text-[#2D008D]" };
  if (idx >= 4 && idx <= 6) return { label: "Middle", color: "bg-[#FFEAC3] text-[#8D5C00]" };
  if (idx >= 7 && idx <= 9) return { label: "Secondary", color: "bg-[#C3FFC3] text-[#008D00]" };
  if (idx >= 10 && idx <= 12) return { label: "Senior", color: "bg-[#FFC3F2] text-[#8D006F]" };

  return { label: "-", color: "bg-[#E6E6E6] text-[#666666]" };
};

export function simulateSchoolSip(
  params: {
    sipAmount: number;
    startIndex: number;
    endIndex: number;
    withdrawIndex: number;
    sipReturn: number;
    swpReturn?: number;
    projectedFees: SchoolFeeProjectionRow[];
    startYear: number;
  },
  schoolOptions: SchoolOptions[],
) {
  const { sipAmount, startIndex, endIndex, withdrawIndex, sipReturn, projectedFees, startYear, swpReturn = 0.06 } = params;

  const sipMonthlyRate = sipReturn / 12;
  const swpMonthlyRate = swpReturn / 12;
  let corpus = 0;
  let currentWithoutFee = 0;

  const feePaidTable: FeePaidTable[] = [];
  const sipGrowthTable: SipGrowthTable[] = [];

  for (let idx = startIndex; idx <= endIndex; idx++) {
    const yearStartCorpus = corpus;
    let yearlySip = 0;
    const yearNo = idx - startIndex + 1;
    
    const isTargetYear = idx >= withdrawIndex;
    const contribution = isTargetYear ? 0 : sipAmount;
    const currentMonthlyRate = isTargetYear ? swpMonthlyRate : sipMonthlyRate;

    let feePaid = 0;
    if (isTargetYear) {
      feePaid = projectedFees[yearNo - 1]?.totalFee ?? 0;
      corpus -= feePaid;
    }

    for (let m = 0; m < 12; m++) {
      corpus = corpus * (1 + currentMonthlyRate) + contribution;
      currentWithoutFee = currentWithoutFee * (1 + currentMonthlyRate) + contribution;
      yearlySip += contribution;
    }

    const academicYear = startYear + yearNo - 1;
    const yearReturns = Math.round(corpus - yearStartCorpus - yearlySip + feePaid);

    if (idx < withdrawIndex) {
      sipGrowthTable.push({
        academicYear,
        classIndex: idx,
        sipAdded: yearlySip,
        returns: yearReturns,
        corpusEnd: corpus,
      });
    }

    feePaidTable.push({
      Class: schoolOptions.find((o) => o.index === idx)?.description ?? `Class ${idx}`,
      "Fee Paid (₹)": feePaid,
      "Corpus End (₹)": Math.round(corpus),
      "Corpus Without Fee": Math.round(currentWithoutFee),
    });
  }

  return { finalCorpus: corpus, feePaidTable, sipGrowthTable };
}

// get tenure information
export const getTenureInfoContent = (goalid?: number, questionId?: number) => {
  switch (goalid) {
    case 4:
      return ["With this Tenure you will achieve your Gadget in"];

    case 1:
      return ["With this Tenure you will achieve your Vehicle in"];

    case 3:
      return ["With this Tenure your Abroad Education is for"];

    case 5: {
      if (questionId === 63) {
        return ["Your Course Duration is"];
      }

      if (questionId === 54) {
        return ["Total Tenure for Course is"];
      }

      return [];
    }

    case 6: {
      return ["With this Tenure you are planning the Marriage in"];
    }

    case 7: {
      if (questionId === 110) {
        return ["Your Current Age is"];
      }

      if (questionId === 112) {
        return ["With this Tenure you are Planning your Retirement for"];
      }

      return [];
    }

    default:
      return [];
  }
};

export const formatLargeINR = (value: number) => {
  if (value >= 1e7) return `${(value / 1e7).toFixed(2)} Cr`;
  if (value >= 1e5) return `${(value / 1e5).toFixed(2)} L`;
  return formatIndianNumber(value);
};

// Get the URL to Go Back
export function getFinancialPlanningBasePath(pathname: string): string {
  const cleanPath = pathname.replace(/^\/|\/$/g, "");

  const parts = cleanPath.split("/");

  if (parts[0] !== "financial-planning") {
    return "/";
  }

  if (parts.length === 1) {
    return "/financial-planning";
  }

  if (parts.length === 2) {
    return "/financial-planning";
  }

  return `/financial-planning/${parts[1]}`;
}

// ======================= HELPERS =======================
export const num = (v: string | number) => Number((v || "0").toString().replace(/,/g, "")) || 0;

export const extractNumber = (v: number | string) => {
  if (!v) return NaN;
  const m = String(v).match(/(\d{1,2})/);
  return m ? Number(m[1]) : NaN;
};

export const parseDate = (d: Date | string): Date | null => {
  if (!d) return null;
  const dt = new Date(d);
  return isNaN(dt.getTime()) ? null : dt;
};

export const calculateChildAge = (dob: Date | null) => {
  if (!dob) return 0;
  const today = new Date();
  let age = today.getFullYear() - dob.getFullYear();
  const m = today.getMonth() - dob.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) age--;
  return Math.max(age, 0);
};

export function calculateSwpMonths({ swpAmount, balance, rate }: CalculateSwpMonthsParams): number {
  if (swpAmount <= 0 || balance <= 0) {
    return 0;
  }

  let months: number = 0;
  const monthlyRate = rate / 12 / 100;

  while (balance > swpAmount) {
    balance += balance * monthlyRate;
    balance -= swpAmount;
    months++;

    if (balance < 0) balance = 0;
    if (months >= 1189) break;
  }

  const tenure = months;
  return tenure;
}

export const bucketAllocationMap = {
  "0-1Cr": { pension: 60, healthEmergency: 25, vacation: 10, legacy: 5 },
  "1-3Cr": { pension: 65, healthEmergency: 20, vacation: 10, legacy: 5 }, // Silver
  "3-5Cr": { pension: 68, healthEmergency: 18, vacation: 9, legacy: 5 }, // Gold
  "5-10Cr": { pension: 70, healthEmergency: 13, vacation: 9, legacy: 8 }, // Platinum
  "10Cr+": { pension: 70, healthEmergency: 13, vacation: 9, legacy: 8 },
} as const;

export function getBucketAllocation(amountInRupees: number) {
  const ONE_CRORE = 10_000_000;
  const THREE_CRORES = 30_000_000;
  const FIVE_CRORES = 50_000_000;
  const TEN_CRORES = 100_000_000;

  if (amountInRupees <= ONE_CRORE) return bucketAllocationMap["0-1Cr"];
  if (amountInRupees <= THREE_CRORES) return bucketAllocationMap["1-3Cr"];
  if (amountInRupees <= FIVE_CRORES) return bucketAllocationMap["3-5Cr"];
  if (amountInRupees <= TEN_CRORES) return bucketAllocationMap["5-10Cr"];
  return bucketAllocationMap["10Cr+"];
}

export const PRE_RETIREMENT_RETURN = 12;
export const PRE_RETIREMENT_LONG_TENURE_RETURN = 15;

export function getPreRetirementReturnRate(sipTenure: number): number {
  return sipTenure > 10 ? PRE_RETIREMENT_LONG_TENURE_RETURN : PRE_RETIREMENT_RETURN;
}

// -------- Age Validation constants --------
export const MIN_CURRENT_AGE = 20;
export const MAX_CURRENT_AGE = 55;
export const DEFAULT_MIN_RETIREMENT_AGE = 45;
export const MAX_RETIREMENT_AGE = 70;

// -------- Post-Retirement (withdrawal phase) constants --------

export const POST_RETIREMENT_PENSION_RATE = 8;
export const POST_RETIREMENT_HEALTH_GROWTH_RATE = 6;
export const POST_RETIREMENT_VACATION_RATE = 10;

export const MIN_RETIREMENT_DURATION = 15;
export const MAX_RETIREMENT_DURATION = 40;
export const DEFAULT_RETIREMENT_DURATION = 20;

export const MIN_CUSTOM_RETIREMENT_CORPUS = 10_00_000;
export const MAX_CUSTOM_RETIREMENT_CORPUS = 100_00_00_000;

// Shared look for the Silver/Gold/Platinum retirement plan cards, used by
// both the Pre-Retirement and Post-Retirement plan-selection screens.
export const PLAN_STYLES: Record<
  NonNullable<Plan>,
  Pick<RetirementPlanCardData, "bgColor" | "border" | "labelColor">
> = {
  "SILVER PLAN": {
    bgColor: "linear-gradient(180deg, #F4F4F4 0%, #CCCCCC 100%)",
    border: "border-[#5E769D]",
    labelColor: "bg-[linear-gradient(90deg,_#A39F99_0%,_#6F6C6C_100%)]",
  },
  "GOLD PLAN": {
    bgColor: "linear-gradient(180deg, #FFF6EA 0%, #F3E9E3 100%)",
    border: "border-[#FBBD5E]",
    labelColor: "bg-[linear-gradient(90deg,_#FBBD5E_0%,_#B16F43_100%)]",
  },
  "PLATINUM PLAN": {
    bgColor: "linear-gradient(180deg, #FAF4FF 0%, #E6D9EF 100%)",
    border: "border-[#8443B2]",
    labelColor: "bg-[linear-gradient(90deg,_#BA5EFB_0%,_#8343B1_100%)]",
  },
};

// Assembles a RetirementPlanCardData, pulling bgColor/border/labelColor from
// PLAN_STYLES so both plan-selection screens stay visually in sync.
export function buildPlanCard(
  id: NonNullable<Plan>,
  label: string,
  corpusText: string,
  withdrawal: string,
  rows: RetirementPlanCardRow[],
): RetirementPlanCardData {
  return {
    id,
    label,
    corpusText,
    withdrawal,
    rows,
    ...PLAN_STYLES[id],
  };
}

const gcd = (a: number, b: number): number => {
  while (b !== 0) {
    [a, b] = [b, a % b];
  }

  return Math.abs(a);
};

export const getClosestPortfolioAmount = (goalAmount: number, allocations: readonly number[]): number => {
  if (goalAmount <= 0) {
    throw new Error("Goal amount must be greater than 0.");
  }

  if (allocations.length === 0) {
    throw new Error("Allocations cannot be empty.");
  }

  const totalAllocation = allocations.reduce((sum, value) => sum + value, 0);

  if (totalAllocation !== 100) {
    throw new Error("Allocations must total 100%.");
  }

  const allocationGcd = allocations.reduce((acc, value) => gcd(acc, value));

  // Portfolio must be divisible by this amount
  const step = 10000 / allocationGcd;

  return Math.round(goalAmount / step) * step;
};

// Computes the pension/health/vacation/legacy bucket split for a given corpus,
// reused by both the Pre-Retirement summary and the Post-Retirement scenario/summary screens.
export function computeRetirementBuckets(
  corpus: number,
  tenure: number,
  allocation: RetirementBucketAllocation,
  legacyGrowthRate: number = PRE_RETIREMENT_LONG_TENURE_RETURN,
): RetirementBucketAmounts {
  const pensionBucket = (corpus * allocation.pension) / 100;
  const healthBucket = (corpus * allocation.healthEmergency) / 100;
  const vacationBucket = (corpus * allocation.vacation) / 100;
  const legacyBucket = (corpus * (allocation.legacy ?? 0)) / 100;

  return {
    pensionBucket,
    healthBucket,
    vacationBucket,
    legacyBucket,
    pensionMonthly: swpMonthly(pensionBucket, POST_RETIREMENT_PENSION_RATE, tenure, "Monthly"),
    healthGrows: lumpsumGrowth(healthBucket, POST_RETIREMENT_HEALTH_GROWTH_RATE, tenure),
    vacationAnnual: swpMonthly(vacationBucket, POST_RETIREMENT_VACATION_RATE, tenure, "Annually"),
    legacyGrows: lumpsumGrowth(legacyBucket, legacyGrowthRate, tenure),
  };
}

export function swpMonthly(
  principal: number,
  annualRate: number,
  years: number,
  withdrawalFrequency: "Monthly" | "Annually",
): number {
  const r = annualRate / 100 / 12;
  const n = withdrawalFrequency === "Monthly" ? years * 12 : years;
  if (r === 0 || principal <= 0) return 0;
  return (principal * r) / (1 - Math.pow(1 + r, -n));
}

export function lumpsumGrowth(principal: number, annualRate: number, years: number): number {
  return principal * Math.pow(1 + annualRate / 100, years);
}

export function calculatedWithdrawAmount({
  investmentAmount,
  tenure,
  rate,
}: {
  investmentAmount: number;
  tenure: number;
  rate: number;
}): number {
  if (investmentAmount <= 0 || tenure <= 0 || rate < 0) {
    return 0;
  }

  const monthlyRate = rate / 12 / 100;
  const totalMonths = tenure * 12;

  if (monthlyRate === 0) {
    return investmentAmount / totalMonths;
  }

  const withdrawal = (investmentAmount * monthlyRate) / (1 - Math.pow(1 + monthlyRate, -totalMonths));

  return Number(withdrawal.toFixed(0));
}
